import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Facebook, Twitter, Instagram, Youtube, Mail, Heart } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Youtube, href: '#', label: 'Youtube' }
  ];

  const quickLinks = [
    { label: t.nav.home, href: '#home' },
    { label: t.nav.vision, href: '#vision' },
    { label: t.nav.pillars, href: '#pillars' },
    { label: t.nav.programs, href: '#programs' },
    { label: t.nav.calendar, href: '#calendar' },
    { label: t.nav.team, href: '#team' },
    { label: t.nav.contact, href: '#contact' }
  ];

  return (
    <footer className="relative bg-gray-950 border-t border-gray-900">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-red-500 rounded-lg flex items-center justify-center font-bold text-white text-xl">
                MRFA
              </div>
              <span className="text-white font-bold text-xl">
                MRFA 2026-2030
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
              {t.footer.tagline}
            </p>
            <div className="flex items-center space-x-2 text-gray-400">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500 fill-current" />
              <span>for Haiti</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                  >
                    <span className="w-0 h-0.5 bg-blue-500 group-hover:w-4 transition-all duration-200" />
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t.footer.followUs}</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-12 h-12 bg-gray-900 border border-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:border-blue-500 hover:bg-blue-500/10 transition-all duration-300 hover:scale-110"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
            <div className="mt-6">
              <a
                href={`mailto:${t.contact.info.email}`}
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm">{t.contact.info.email}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Haiti Flag Divider */}
        <div className="grid grid-cols-2 gap-2 h-2 mb-8 rounded overflow-hidden">
          <div className="bg-blue-600" />
          <div className="bg-red-600" />
        </div>

        {/* Football Gallery */}
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-8">
          <img src="https://images.unsplash.com/photo-1629977009792-9ea41c019ea2?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Action" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
          <img src="https://images.unsplash.com/photo-1757032542526-3594292ba021?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Save" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
          <img src="https://images.unsplash.com/photo-1762028335603-37e338a90298?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Goal" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
          <img src="https://images.unsplash.com/photo-1700914299576-fb86d797b37d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Field" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
          <img src="https://images.unsplash.com/photo-1752673508944-2578f0d62ce1?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Ball" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
          <img src="https://images.unsplash.com/photo-1764703666646-acc2f7d48857?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Stadium" className="w-full h-20 object-cover rounded opacity-60 hover:opacity-100 transition-opacity" />
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-gray-900">
          <p className="text-gray-500 text-sm mb-4 md:mb-0">
            {t.footer.copyright}
          </p>
          <div className="flex items-center space-x-6">
            <a href="#" className="text-gray-500 hover:text-white text-sm transition-colors duration-200">
              Politique de confidentialité
            </a>
            <a href="#" className="text-gray-500 hover:text-white text-sm transition-colors duration-200">
              Conditions d'utilisation
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
